import './styles/main.scss';

import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { gsap } from 'gsap';

import { BackgroundManager } from './background/BackgroundManager.js';
import { setBackgroundManager } from './background/backgroundInstance.js';
import { AudioManager } from './audio/AudioManager.js';
import { StationIndicator } from './audio/StationIndicator.js';
import { Sidebar } from './nav/Sidebar.js';
import { Carousel } from './carousel/Carousel.js';
import { OffAir } from './offair/OffAir.js';
import { OffAirTitle } from './offair/OffAirTitle.js';
import { ChannelSurf } from './nav/ChannelSurf.js';
import { LiveChannel } from './background/LiveChannel.js';
import { StandBy } from './ui/StandBy.js';
import { Ticker } from './ui/Ticker.js';
import { Magnet } from './ui/Magnet.js';
import { Notice } from './ui/Notice.js';
import { wrapWords } from './ui/wordBars.js';
import { HeroName } from './hero/HeroName.js';
import { initAnalytics, track } from './analytics.js';
initAnalytics();
import { TransitionManager } from './transitions/TransitionManager.js';
import { WORLDS_SECTION_ID } from './nav/anchors.js';
import { WorldManager } from './worlds/WorldManager.js';
import { transitionLog } from './debug/transitionDebug.js';
import { runChannelWipe } from './transitions/channelWipe.js';
import { toCanvas } from 'html-to-image';
import { wantsSurfGame } from './worlds/tbd/surf/detect.js';

function labelHandheldGame() {
  if (!wantsSurfGame()) return;
  const card = document.querySelector('.card[data-world="mild-concern"]');
  const title = card?.querySelector('.card__title');
  const desc = card?.querySelector('.card__desc');
  if (title) title.textContent = 'Channel Surfing';
  if (desc) desc.textContent = 'Ride the broadcast';
  const menu = document.querySelector('#site-menu a[href="/mild-concern/"]');
  if (menu) {
    const num = menu.querySelector('.menu__num');
    menu.textContent = '';
    if (num) menu.append(num);
    menu.append('Channel Surfing');
  }
}

function boot() {
  transitionLog('main:boot', { path: location.pathname, hash: location.hash, readyState: document.readyState });
  // ── Smooth scroll ──────────────────────────────────────────
  // Touch already scrolls natively. Lenis on a coarse pointer adds a second
  // scroll clock next to the card transforms and is what makes the stack hitch.
  const handheld = wantsSurfGame();
  const lenis = handheld ? null : new Lenis({
    lerp: 0.08,
    smoothWheel: true,
    syncTouch: false,
    autoRaf: false,
  });
  if (lenis) {
    window.lenis = lenis;
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
  }
  gsap.ticker.lagSmoothing(0);

  // ── Page modules ───────────────────────────────────────────
  const background = new BackgroundManager();
  setBackgroundManager(background);
  const audio = new AudioManager();
  window.siteAudio = audio; // debugging handle
  new StationIndicator(audio);
  new Sidebar();
  WorldManager.init();

  // Carousel — card clicks use image expand + world open / MPA nav.
  new Carousel(document.getElementById('carousel'));

  // Handheld serves Channel Surf, so the card and the menu use that name
  // before the titles are split into word plates.
  labelHandheldGame();

  if (handheld) {
    const bg = document.getElementById('bg-video');
    if (bg) {
      let scrollT = 0;
      let held = false;
      window.addEventListener('scroll', () => {
        if (document.body.classList.contains('is-world-open')) return;
        if (!held) { held = true; bg.pause?.(); }
        clearTimeout(scrollT);
        scrollT = setTimeout(() => {
          held = false;
          if (!document.body.classList.contains('is-world-open')) bg.play?.().catch(() => {});
        }, 180);
      }, { passive: true });
    }
  }

  // Word bars: every word of the small lines + card titles on its own plate.
  // Off-air title is split per-letter (OffAirTitle) like the hero wordmark,
  // so it stays out of wrapWords — the supporting lines still get bars.
  wrapWords('.hero__pre, .hero__tags, .hero__loc, .hero__contact-avail, .card__title, .card__desc, .carousel-section__label, .offair__pre, .offair__fine, .offair__links span');

  // Channel surfing: number keys / arrows tune worlds, with a TV-style OSD.
  window.channelSurf = new ChannelSurf({ cards: Array.from(document.querySelectorAll('#carousel .card')) });
  // CH 09: the visitor's webcam through the same signal path (local only).
  window.liveChannel = new LiveChannel({ background, button: document.getElementById('live-toggle'), osd: window.channelSurf });
  // Idle → "PLEASE STAND BY"; hero chyron.
  new StandBy({ background });
  new Notice();
  const heroEl = document.getElementById('hero');
  // Ticker lives on <body>: it is position:fixed and must not inherit a transformed ancestor
  if (heroEl) { new Ticker(document.body, { channelSurf: window.channelSurf }); new Magnet(heroEl, { background }); }

  // Off Air sign-off section — fades the live background under it.
  const offairEl = document.getElementById('offair');
  if (offairEl) {
    new OffAir(offairEl, { background: document.getElementById('background'), video: document.getElementById('bg-video'), audio });
    new OffAirTitle({ root: offairEl.querySelector('.offair__title'), section: offairEl });
  }

  // ── Hero reveal + per-letter scroll lag ────────────────────
  // Reveal animates the wrappers (transform: y) so all chars move together.
  // After clearProps fires, HeroName takes over per-letter transforms via
  // its own rAF tick — the two never fight because they target different
  // elements (wrappers vs char spans).
  const revealHero = () => {
    const name = document.querySelector('.name');
    if (!name) return;
    const parts = name.querySelectorAll('.name__solid, .name__ghost');
    gsap.fromTo(
      parts,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.08,
        clearProps: 'transform',
        onComplete: () => {
          // Split + start scroll-lag chase only after reveal finishes,
          // so the per-letter transforms don't compete with the GSAP tween.
          new HeroName(name);
        },
      }
    );
  };
  if (document.fonts?.ready) {
    document.fonts.ready.then(revealHero);
  } else {
    revealHero();
  }

  // ── Audio toggle button ────────────────────────────────────
  const audioBtn = document.getElementById('audio-toggle');
  if (audioBtn) {
    const syncButton = (state) => {
      const on = state.enabled;
      audioBtn.setAttribute('aria-pressed', String(on));
      audioBtn.setAttribute('aria-label', on ? 'Mute audio' : 'Enable audio');
      audioBtn.setAttribute('title', on ? 'Mute audio' : 'Enable audio');
    };
    syncButton({ enabled: audio.isEnabled });
    audio.onChange(syncButton);

    audioBtn.addEventListener('click', () => audio.toggle());
  }

  // ── Temp randomizer wiring (will move into Randomizer.js in Phase 2) ──
  // Theme color + font swap is handled inside BackgroundManager.setPreset()
  // which writes data-theme on <body> in lockstep with the glitchGL preset
  // change. This handler just sequences: preset cycle → video swap → SFX.
  // Tape audio: when a clip with a soundtrack lands, sometimes let it play
  // instead of the station (only while sound is on; the toggle still mutes).
  const TAPE_ODDS = 0.4;
  document.addEventListener('bg:clip-audio', (e) => {
    const { video, name, hasAudio } = e.detail;
    if (hasAudio && Math.random() < TAPE_ODDS) audio.duck(video, { name });
    else if (audio.isOnTape) audio.duck(null);
  });
  document.addEventListener('bg:source-change', () => { if (audio.isOnTape) audio.duck(null); });

  // The game has its own sound — the station fades out when it opens and
  // comes back when you leave (unless you toggled audio yourself meanwhile).
  document.addEventListener('world:open', (e) => { if (e.detail.world === 'mild-concern' && audio.isEnabled) audio.fadeOut(900); });
  document.addEventListener('world:open', (e) => track('world_open', { world: e.detail.world }));
  document.addEventListener('world:close', (e) => { if (e.detail.world === 'mild-concern') audio.resumeAfterFade(); });

  document.addEventListener('randomize', () => {
    background.randomize();
    audio.rotate();
    audio.playEffect('./sounds/channel.mp3', { volume: 0.2 });
  });

  // ── Loader, then TransitionManager init (bfcache hooks; no WebGL) ──
  // Wait for fonts + window.load, remove the full-screen wordmark, then run
  // `TransitionManager.init()`.
  const dismissLoader = async () => {
    const loader = document.getElementById('loader');
    if (!loader) {
      transitionLog('main:loader_absent', {});
      return;
    }
    transitionLog('main:loader_dismiss_start', {});
    // Make sure the video has real frames before we tune in to it.
    try { await background.prepareForHomeTransition(); } catch { /* ignore */ }
    let snap = null;
    try {
      snap = await toCanvas(loader, {
        backgroundColor: '#070707',
        width: window.innerWidth,
        height: window.innerHeight,
      });
    } catch { /* wipe from solid */ }
    await runChannelWipe({
      source: snap,
      fit: 'stretch',
      coverLayer: loader,
      zIndex: 10001,
      className: 'world-channel-wipe world-channel-wipe--entry',
    });
    transitionLog('main:loader_dismiss_done', {});
  };
  const ready = [
    document.fonts?.ready ?? Promise.resolve(),
    new Promise((resolve) => {
      if (document.readyState === 'complete') resolve();
      else window.addEventListener('load', resolve, { once: true });
    }),
  ];
  transitionLog('main:await_ready', {});
  Promise.all(ready)
    .then(() => {
      transitionLog('main:ready_all', {});
      return dismissLoader();
    })
    .then(() => {
      transitionLog('main:transitionManager_init', {});
      return TransitionManager.init();
    })
    .then(() => {
      transitionLog('main:tm_init_resolved', {});
      if (location.hash !== `#${WORLDS_SECTION_ID}`) return;
      const el = document.getElementById(WORLDS_SECTION_ID);
      if (!el) return;
      transitionLog('main:lenis_scroll_worlds', {});
      if (lenis) lenis.scrollTo(el, { offset: -4, duration: 0.6 });
      else el.scrollIntoView();
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
